import React, { useState } from "react";
import { DatePicker, Space, Button, Table, Tabs, Typography, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
  useInventoryReportsServiceGetInvoiceAgingReport,
  useInventoryReportsServiceDownloadInvoiceAgingReport,
} from "api/queries";
import dayjs from "dayjs";
import { motion } from "framer-motion";

const { Title } = Typography;

const InvoiceAgingReport = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [periodFilter, setPeriodFilter] = useState("This Month");

  const { data } = useInventoryReportsServiceGetInvoiceAgingReport({
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  });
  const { mutate: getDownloadUrl, isPending } =
    useInventoryReportsServiceDownloadInvoiceAgingReport();

  const overdueInvoices = (data?.overdue || []).map((item: any, idx: number) => ({
    key: item.invoiceNumber || idx,
    invoiceNumber: item.invoiceNumber,
    customerName: item.customerName,
    amount: item.amount,
    dueDate: item.dueDate,
    daysOverdue: item.daysOverdue,
  }));
  const upcomingInvoices = (data?.upcoming || []).map((item: any, idx: number) => ({
    key: item.invoiceNumber || idx,
    invoiceNumber: item.invoiceNumber,
    customerName: item.customerName,
    amount: item.amount,
    dueDate: item.dueDate,
    daysUntilDue: item.daysUntilDue,
  }));

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
    },
    { title: "Customer", dataIndex: "customerName", key: "customerName" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value: number) => `${value.toLocaleString()}`,
    },
    { title: "Due Date", dataIndex: "dueDate", key: "dueDate" },
  ];

  // Handle period filter change
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
  };

  const handleDownload = () => {
    getDownloadUrl(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: { downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "invoice_aging_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  const items = [
    {
      key: "1",
      label: "Overdue Invoices",
      children: (
        <Table
          columns={[...columns, { title: "Days Overdue", dataIndex: "daysOverdue", key: "daysOverdue" }]}
          dataSource={overdueInvoices}
          pagination={{ pageSize: 10 }}
          loading={isPending}
          scroll={{ x: "max-content" }}
        />
      ),
    },
    {
      key: "2",
      label: "Upcoming Due Invoices",
      children: (
        <Table
          columns={[...columns, { title: "Days Until Due", dataIndex: "daysUntilDue", key: "daysUntilDue" }]}
          dataSource={upcomingInvoices}
          pagination={{ pageSize: 10 }}
          loading={isPending}
          scroll={{ x: "max-content" }}
        />
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Invoice Aging Report</Title>
          <Space>
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              <Select.Option value="Today">Today</Select.Option>
              <Select.Option value="This Week">This Week</Select.Option>
              <Select.Option value="This Month">This Month</Select.Option>
              <Select.Option value="This Year">This Year</Select.Option>
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>
        <Tabs items={items} />
      </Space>
    </motion.div>
  );
};

export default InvoiceAgingReport;
